"use client";

import {
  Alert,
  Card,
  Heading,
  Select,
  Stack,
  Text,
} from "@softmax/design-system";
import { useId } from "react";

export type PolicySelectionScope = "any_request" | "owner_visible";
export type PolicySelectionAccessProps = {
  readonly value: PolicySelectionScope;
  readonly pending: boolean;
  readonly error: string | null;
  readonly onChange: (scope: PolicySelectionScope) => void;
};
const options: readonly {
  readonly value: PolicySelectionScope;
  readonly label: string;
}[] = [
  { value: "any_request", label: "Any requester" },
  { value: "owner_visible", label: "Only requests you can view" },
];

export function PolicySelectionAccess({
  value,
  pending,
  error,
  onChange,
}: PolicySelectionAccessProps) {
  const headingId = useId();
  return (
    <Card>
      <Stack width="auto">
        <Heading level={2} id={headingId}>
          Who can use this policy in new games?
        </Heading>
        <Text tone="muted">
          This policy-wide setting changes future selection only. It does not
          grant access to requests or episodes.
        </Text>
        <Select
          aria-label="Who can use this policy in new games?"
          options={options}
          value={value}
          disabled={pending}
          onValueChange={onChange}
        />
        <Text size="small" tone="muted">
          {value === "any_request"
            ? "Other players may use this policy in public or private requests."
            : "Other players may use this policy only in requests visible to you."}
        </Text>
        {pending && (
          <output>
            <Text tone="muted">Saving selection access…</Text>
          </output>
        )}
        {error && <Alert tone="danger">{error}</Alert>}
      </Stack>
    </Card>
  );
}
